import clsx from "clsx";
import { Link, useNavigate } from "react-router-dom";
import ExpenserTriangle from "../components/ExpenserTriangle/ExpenserTriangle";
import {
  MdAccountBox,
  MdCached,
  MdDarkMode,
  MdLightMode,
  MdLogout,
  MdNoteAlt,
  MdPersonSearch,
  MdSettings,
  MdOutlineAttachment,
  MdOutlineQrCode2,
  MdNotificationsNone,
  MdNotificationsActive,
  MdConnectWithoutContact,
} from "react-icons/md";
import { useTranslation } from "react-i18next";

import { FILE_STORAGE_SERVER_URL, ICON_SIZES } from "../helpers/constants";
import { getShortName } from "../helpers/utils";
import modalsStore from "../store/modalsStore";
import useInvites from "../hooks/useInvites";
import useCurrentUser from "../hooks/useCurrentUser";
import useMainFilter from "../hooks/useMainFilter";
import ButtonGroup from "../components/ButtonGroup/ButtonGroup";
import { forwardRef, ReactNode, useMemo, useState } from "react";
import useDarkMode from "../hooks/useDarkMode";
import { orderBy } from "lodash";
import useRecurringTransactions from "src/hooks/useRecurringTransactions";
import useAllTransactions from "src/hooks/useAllTransactions";

interface IMobileTopNavigation {
  children?: ReactNode;
}

const AVATAR_SIZE = 32;

const MobileTopNavigation = forwardRef<HTMLDivElement, IMobileTopNavigation>(
  ({ children }, ref) => {
    const { t } = useTranslation();
    const [isCleaningCache] = useState<boolean>(false);
    const { receivedInvites } = useInvites();
    const { allPendingTransactions, allReceivedTransactions } =
      useAllTransactions();
    const navigate = useNavigate();
    const { setShowLogoutDialog, setShowContactQrCode } = modalsStore();
    const { mainFilter } = useMainFilter();
    const { userInfo: currentUserInfo } = useCurrentUser();
    const { recentOverduePayments, todayPayments, upcomingPayments } =
      useRecurringTransactions(false);

    const { isEnabled, setEnabled } = useDarkMode();

    const hasNotifications = useMemo(() => {
      return (
        allPendingTransactions?.length +
        recentOverduePayments?.length +
        upcomingPayments?.length +
        todayPayments?.length +
        allReceivedTransactions?.length
      );
    }, [
      allPendingTransactions,
      recentOverduePayments,
      upcomingPayments,
      todayPayments,
      allReceivedTransactions,
    ]);

    const menuOptions = useMemo(() => {
      return orderBy(
        [
          {
            text: t("Datos de contacto"),
            onClick: () => setShowContactQrCode(true),
            icon: <MdOutlineQrCode2 size={ICON_SIZES.MD} />,
          },
          {
            text: t("menu.notes"),
            onClick: () => {
              navigate("/notes");
            },
            icon: (
              <MdNoteAlt size={ICON_SIZES.MD} className="text-yellow-400" />
            ),
            hide: true,
          },
          {
            text: t("Archivos"),
            onClick: () => {
              navigate(`/attachments/${mainFilter?.account}`);
            },
            icon: (
              <MdOutlineAttachment
                size={ICON_SIZES.MD}
                className="text-green-400"
              />
            ),
            hide: false,
          },
          {
            text: t("Contactos"),
            onClick: () => {
              navigate(`/contacts`);
            },
            icon: (
              <MdConnectWithoutContact
                size={ICON_SIZES.MD}
                className="text-fuchsia-500"
              />
            ),
            hide: true,
          },
          {
            text: t("menu.settings"),
            onClick: () => {
              navigate("/settings");
            },
            icon: <MdSettings size={ICON_SIZES.MD} className="text-blue-600" />,
          },

          {
            text: t("menu.contacts"),
            onClick: () => navigate("/contacts"),
            icon: (
              <MdPersonSearch
                className="text-fuchsia-500"
                size={ICON_SIZES.MD}
              />
            ),
            hide: true,
          },
          {
            text: t("shared.clear_cache"),
            onClick: async () => {
              navigate("/settings/cache");
            },
            icon: <MdCached size={ICON_SIZES.MD} className="text-red-400" />,
            isLoading: isCleaningCache,
            hide: true,
          },
          {
            text: t(!isEnabled ? "menu.dark_mode" : "menu.light_mode"),
            onClick: async () => {
              setEnabled(!isEnabled);
            },
            icon: !isEnabled ? (
              <MdDarkMode size={ICON_SIZES.MD} className="text-purple-400" />
            ) : (
              <MdLightMode size={ICON_SIZES.MD} className="text-yellow-400" />
            ),
          },
        ],
        ["text"],
        ["asc"]
      );
    }, [mainFilter?.account, isCleaningCache, isEnabled]);

    return (
      <div
        ref={ref}
        className={clsx([
          "fixed",
          "md:hidden",
          "top-0",
          "left-0",
          "right-0",
          "!z-[17]",
          // "bg-[#0e223f] dark:bg-gray-900 dark:md:bg-gray-900/50",
          "bg-blue-900 dark:bg-gray-900 dark:md:bg-gray-900/50",
          "flex",
          "flex-col",
          "w-full",
          "items-center",
          "justify-center",
          "px-4",
          "py-2",
          // "min-h-[66px]",
          // "max-h-[66px]",
          "min-h-[calc(66px+env(safe-area-inset-top))]",
          "max-h-[calc(66px+env(safe-area-inset-top))]",
          "pt-[calc(8px+env(safe-area-inset-top))]",
        ])}
      >
        <div
          className={clsx([
            "flex",
            "flex-row",
            "gap-2",
            "w-full",
            "rounded-md",
            "justify-between",
          ])}
        >
          <Link
            to={`/reports/${mainFilter?.account}`}
            className={clsx([
              "flex",
              "flex-row",
              "items-center",
              "md:justify-end",
              "gap-1",
            ])}
          >
            <ExpenserTriangle
              size={ICON_SIZES.MD}
              className={clsx(["text-red-600"])}
            />
            <h1
              className={clsx([
                "text-2xl",
                "font-medium",
                // "text-[#0e223f]",
                "text-white",
                "dark:text-white",
              ])}
            >
              eXpenser
              <span
                className={clsx([
                  "text-sm",
                  "uppercase",
                  "text-red-600",
                  {
                    hidden: false, //isPwa(),
                  },
                ])}
              >
                .net
              </span>
            </h1>
          </Link>
          <ButtonGroup
            gap={1}
            buttons={[
              {
                onClick: () => {
                  navigate(`/notifications`);
                },
                icon: !!hasNotifications ? (
                  <MdNotificationsActive
                    className={clsx(["text-white dark:text-cyan-400"])}
                    size={ICON_SIZES.MD}
                  />
                ) : (
                  <MdNotificationsNone
                    className={clsx([
                      "opacity-60 text-white dark:text-cyan-400",
                    ])}
                    size={ICON_SIZES.MD}
                  />
                ),
                notify: !!hasNotifications,
                className: clsx(["!bg-transparent", "!border-transparent"]),
              },
              {
                icon: currentUserInfo?.picture ? (
                  <img
                    src={`${FILE_STORAGE_SERVER_URL}/profile-thumbnail-${currentUserInfo?.picture}`}
                    alt={currentUserInfo?.name || "eXpenser.net"}
                    className={clsx([
                      "flex",
                      `!min-w-[32px]`,
                      `!max-w-[32px]`,
                      `!min-h-[32px]`,
                      `!max-h-[32px]`,
                      "w-full",
                      "rounded-full",
                      "bg-checkers",
                    ])}
                  />
                ) : (
                  <MdAccountBox size={ICON_SIZES.MD} />
                ),
                hide: !getShortName(currentUserInfo?.name),
                className: clsx(["!bg-transparent", "!border-transparent"]),
                options: [
                  ...menuOptions,
                  {
                    text: t("menu.signout"),
                    onClick: () => {
                      setShowLogoutDialog(true);
                      // navigate("/logout");
                    },
                    icon: (
                      <MdLogout
                        size={ICON_SIZES.MD}
                        className="text-purple-600"
                      />
                    ),
                  },
                ],
              },
            ]}
            align="right"
          />
        </div>
        {!!children && (
          <div className={clsx(["flex", "w-full"])}>{children}</div>
        )}
      </div>
    );
  }
);

export default MobileTopNavigation;
